import { DEBUG_CONFIG } from '../constants/debugConfig.ts';

/**
 * 디버깅 유틸리티 함수들
 */

/**
 * 실제 매출 자동화 디버깅 로거
 */
export class ActualSalesDebugLogger {
  private static isEnabled() {
    return DEBUG_CONFIG.ACTUAL_SALES.ENABLED;
  }

  /**
   * 훅 관련 디버깅 로그
   */
  static hook(message: string, data?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.HOOK) {
      console.log(`🔗 [ActualSales-Hook] ${message}`, data || '');
    }
  }

  /**
   * 서비스 관련 디버깅 로그
   */
  static service(message: string, data?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.SERVICE) {
      console.log(`⚙️ [ActualSales-Service] ${message}`, data || '');
    }
  }

  /**
   * UI 렌더링 관련 디버깅 로그
   */
  static ui(message: string, data?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.UI) {
      console.log(`🎨 [ActualSales-UI] ${message}`, data || '');
    }
  }

  /**
   * 캐시 관련 디버깅 로그
   */
  static cache(message: string, data?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.CACHE) {
      console.log(`💾 [ActualSales-Cache] ${message}`, data || '');
    }
  }

  /**
   * Firebase 데이터 로딩 관련 디버깅 로그
   */
  static firebase(message: string, data?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.FIREBASE) {
      console.log(`🔥 [ActualSales-Firebase] ${message}`, data || '');
    }
  }

  /**
   * 계산 과정 관련 디버깅 로그
   */
  static calculation(message: string, data?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.CALCULATION) {
      console.log(`🧮 [ActualSales-Calculation] ${message}`, data || '');
    }
  }

  /**
   * 어시스트별 상세 데이터 관련 디버깅 로그
   */
  static assistDetail(message: string, data?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.ASSIST_DETAIL) {
      console.log(`👤 [ActualSales-AssistDetail] ${message}`, data || '');
    }
  }

  /**
   * 팀별 집계 관련 디버깅 로그
   */
  static teamAggregation(message: string, data?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.TEAM_AGGREGATION) {
      console.log(`👥 [ActualSales-TeamAggregation] ${message}`, data || '');
    }
  }

  /**
   * 성능 측정 관련 디버깅 로그
   */
  static performance(message: string, data?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.PERFORMANCE) {
      console.log(`⚡ [ActualSales-Performance] ${message}`, data || '');
    }
  }

  /**
   * 시간 측정 관련 디버깅 로그
   */
  static timing(message: string, startTime?: number, endTime?: number) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.TIMING) {
      if (startTime && endTime) {
        const duration = Math.round(endTime - startTime);
        console.log(`⏱️ [ActualSales-Timing] ${message} - ${duration}ms`);
      } else {
        console.log(`⏱️ [ActualSales-Timing] ${message}`);
      }
    }
  }

  /**
   * 데이터 유효성 검사 관련 디버깅 로그
   */
  static validation(message: string, data?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.VALIDATION) {
      console.log(`✅ [ActualSales-Validation] ${message}`, data || '');
    }
  }

  /**
   * 에러 처리 관련 디버깅 로그
   */
  static error(message: string, error?: any) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.ERROR_DETAILS) {
      console.error(`❌ [ActualSales-Error] ${message}`, error || '');
    }
  }

  /**
   * 데이터 플로우 추적을 위한 단계별 로그
   */
  static step(stepNumber: number, stepName: string, data?: any) {
    if (this.isEnabled()) {
      console.log(`📋 [ActualSales-Step${stepNumber}] ${stepName}`, data || '');
    }
  }

  /**
   * 중요한 비즈니스 로직 관련 로그
   */
  static business(message: string, data?: any) {
    if (this.isEnabled()) {
      console.log(`🏢 [ActualSales-Business] ${message}`, data || '');
    }
  }

  /**
   * 데이터 변환 과정 관련 로그
   */
  static transformation(message: string, beforeData?: any, afterData?: any) {
    if (this.isEnabled()) {
      console.log(`🔄 [ActualSales-Transformation] ${message}`, {
        before: beforeData,
        after: afterData
      });
    }
  }

  /**
   * 그룹화된 로그 시작
   */
  static groupStart(groupName: string) {
    if (this.isEnabled()) {
      console.group(`📁 [ActualSales-Group] ${groupName}`);
    }
  }

  /**
   * 그룹화된 로그 종료
   */
  static groupEnd() {
    if (this.isEnabled()) {
      console.groupEnd();
    }
  }

  /**
   * 테이블 형태로 데이터 출력
   */
  static table(data: any[], title?: string) {
    if (this.isEnabled()) {
      if (title) {
        console.log(`📊 [ActualSales-Table] ${title}`);
      }
      console.table(data);
    }
  }

  /**
   * 메모리 사용량 체크
   */
  static memory(message: string) {
    if (this.isEnabled() && DEBUG_CONFIG.ACTUAL_SALES.PERFORMANCE) {
      if (typeof performance !== 'undefined' && performance.memory) {
        const memory = performance.memory;
        console.log(`🧠 [ActualSales-Memory] ${message}`, {
          used: Math.round(memory.usedJSHeapSize / 1024 / 1024) + 'MB',
          total: Math.round(memory.totalJSHeapSize / 1024 / 1024) + 'MB',
          limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) + 'MB'
        });
      }
    }
  }
}

/**
 * 성능 측정 유틸리티
 */
export class PerformanceTracker {
  private static timers = new Map<string, number>();

  /**
   * 성능 측정 시작
   */
  static start(name: string) {
    if (DEBUG_CONFIG.ACTUAL_SALES.ENABLED && DEBUG_CONFIG.ACTUAL_SALES.PERFORMANCE) {
      this.timers.set(name, performance.now());
      ActualSalesDebugLogger.timing(`성능 측정 시작: ${name}`);
    }
  }

  /**
   * 성능 측정 종료
   */
  static end(name: string) {
    if (DEBUG_CONFIG.ACTUAL_SALES.ENABLED && DEBUG_CONFIG.ACTUAL_SALES.PERFORMANCE) {
      const startTime = this.timers.get(name);
      if (startTime) {
        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);
        ActualSalesDebugLogger.timing(`성능 측정 완료: ${name}`, startTime, endTime);
        this.timers.delete(name);
        return duration;
      }
    }
    return 0;
  }

  /**
   * 현재 진행 중인 성능 측정들 확인
   */
  static getActiveTimers() {
    if (DEBUG_CONFIG.ACTUAL_SALES.ENABLED) {
      return Array.from(this.timers.keys());
    }
    return [];
  }
}

/**
 * 일간 보고서 페이지 전용 디버깅 로거
 */
export class DailyReportDebugLogger {
  private static DEBUG_MODE = {
    EXCEL_UPLOAD: false,
    ERROR_HANDLING: false,
    LOADING_DEBUG: false,
    MONTHLY_CALCULATION: false,
    CHART_MATCHING: false,
    EXPENSE_HANDLING: false,
    FIREBASE_SAVE: false,
    AUTO_SAVE: false,
    ALL: false
  };

  static log(category: keyof typeof DailyReportDebugLogger.DEBUG_MODE, ...args: any[]) {
    if (this.DEBUG_MODE.ALL || this.DEBUG_MODE[category]) {
      console.log(...args);
    }
  }

  static warn(category: keyof typeof DailyReportDebugLogger.DEBUG_MODE, ...args: any[]) {
    if (this.DEBUG_MODE.ALL || this.DEBUG_MODE[category]) {
      console.warn(...args);
    }
  }

  static error(category: keyof typeof DailyReportDebugLogger.DEBUG_MODE, ...args: any[]) {
    if (this.DEBUG_MODE.ALL || this.DEBUG_MODE[category]) {
      console.error(...args);
    }
  }

  static loadingState(action: 'START' | 'END', location: string, additionalInfo?: string) {
    if (this.DEBUG_MODE.ALL || this.DEBUG_MODE.LOADING_DEBUG) {
      const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
      const emoji = action === 'START' ? '🔄' : '✅';
      const actionText = action === 'START' ? '로딩 시작' : '로딩 해제';
      const info = additionalInfo ? ` - ${additionalInfo}` : '';
      console.log(`${emoji} [${timestamp}] [로딩 디버깅] ${actionText}: ${location}${info}`);
    }
  }

  static setDebugMode(category: keyof typeof DailyReportDebugLogger.DEBUG_MODE, enabled: boolean) {
    this.DEBUG_MODE[category] = enabled;
  }

  static enableAll() {
    Object.keys(this.DEBUG_MODE).forEach(key => {
      (this.DEBUG_MODE as any)[key] = true;
    });
  }

  static disableAll() {
    Object.keys(this.DEBUG_MODE).forEach(key => {
      (this.DEBUG_MODE as any)[key] = false;
    });
  }
}

/**
 * 데이터 유효성 검사 유틸리티
 */
export class DataValidator {
  /**
   * 어시스트별 상세 데이터 유효성 검사
   */
  static validateAssistDetails(assistDetails: Record<string, any>) {
    if (!DEBUG_CONFIG.ACTUAL_SALES.ENABLED || !DEBUG_CONFIG.ACTUAL_SALES.VALIDATION) {
      return true;
    }

    let isValid = true;
    const issues: string[] = [];

    Object.entries(assistDetails).forEach(([assistName, details]) => {
      if (!details.teamName) {
        issues.push(`${assistName}: 팀명이 없습니다`);
        isValid = false;
      }
      if (typeof details.receipts !== 'number') {
        issues.push(`${assistName}: 수납액이 숫자가 아닙니다`);
        isValid = false;
      }
      if (typeof details.expenses !== 'number') {
        issues.push(`${assistName}: 지출액이 숫자가 아닙니다`);
        isValid = false;
      }
      if (typeof details.profit !== 'number') {
        issues.push(`${assistName}: 순수익이 숫자가 아닙니다`);
        isValid = false;
      }
    });

    if (issues.length > 0) {
      ActualSalesDebugLogger.validation('어시스트별 상세 데이터 유효성 검사 실패', issues);
    } else {
      ActualSalesDebugLogger.validation('어시스트별 상세 데이터 유효성 검사 통과', {
        assistCount: Object.keys(assistDetails).length
      });
    }

    return isValid;
  }

  /**
   * 팀별 실제매출 데이터 유효성 검사
   */
  static validateTeamActualSales(teamActualSales: Record<string, number>) {
    if (!DEBUG_CONFIG.ACTUAL_SALES.ENABLED || !DEBUG_CONFIG.ACTUAL_SALES.VALIDATION) {
      return true;
    }

    let isValid = true;
    const issues: string[] = [];

    Object.entries(teamActualSales).forEach(([teamName, actualSales]) => {
      if (typeof actualSales !== 'number') {
        issues.push(`${teamName}: 실제매출이 숫자가 아닙니다`);
        isValid = false;
      }
      if (actualSales < 0) {
        issues.push(`${teamName}: 실제매출이 음수입니다`);
        isValid = false;
      }
    });

    if (issues.length > 0) {
      ActualSalesDebugLogger.validation('팀별 실제매출 데이터 유효성 검사 실패', issues);
    } else {
      ActualSalesDebugLogger.validation('팀별 실제매출 데이터 유효성 검사 통과', {
        teamCount: Object.keys(teamActualSales).length
      });
    }

    return isValid;
  }
}

 

/**
 * 개발자 도구에서 사용할 수 있는 전역 디버깅 컨트롤러
 */
export const ActualSalesDebug = {
  // 현재 디버깅 상태 확인
  getStatus: () => {
    console.table({
      'Master Switch': DEBUG_CONFIG.ACTUAL_SALES.ENABLED,
      'Hook': DEBUG_CONFIG.ACTUAL_SALES.HOOK,
      'Service': DEBUG_CONFIG.ACTUAL_SALES.SERVICE,
      'UI': DEBUG_CONFIG.ACTUAL_SALES.UI,
      'Cache': DEBUG_CONFIG.ACTUAL_SALES.CACHE,
      'Firebase': DEBUG_CONFIG.ACTUAL_SALES.FIREBASE,
      'Calculation': DEBUG_CONFIG.ACTUAL_SALES.CALCULATION,
      'Assist Detail': DEBUG_CONFIG.ACTUAL_SALES.ASSIST_DETAIL,
      'Team Aggregation': DEBUG_CONFIG.ACTUAL_SALES.TEAM_AGGREGATION,
      'Performance': DEBUG_CONFIG.ACTUAL_SALES.PERFORMANCE,
      'Timing': DEBUG_CONFIG.ACTUAL_SALES.TIMING,
      'Validation': DEBUG_CONFIG.ACTUAL_SALES.VALIDATION,
      'Error Details': DEBUG_CONFIG.ACTUAL_SALES.ERROR_DETAILS
    });
  },

  // 특정 모듈 디버깅 활성화/비활성화
  setModule: (module: string, enabled: boolean) => {
    const moduleKey = module.toUpperCase();
    if (DEBUG_CONFIG.ACTUAL_SALES.hasOwnProperty(moduleKey) && moduleKey !== 'ENABLED') {
      (DEBUG_CONFIG.ACTUAL_SALES as any)[moduleKey] = enabled;
      console.log(`🔧 ${module} 디버깅 ${enabled ? '활성화' : '비활성화'}`);
    } else {
      console.warn(`❌ 유효하지 않은 모듈명: ${module}`);
      console.log('사용 가능한 모듈:', Object.keys(DEBUG_CONFIG.ACTUAL_SALES).filter(k => k !== 'ENABLED'));
    }
  },

  // 모든 디버깅 활성화
  enableAll: () => {
    Object.keys(DEBUG_CONFIG.ACTUAL_SALES).forEach(key => {
      if (key !== 'ENABLED') {
        (DEBUG_CONFIG.ACTUAL_SALES as any)[key] = true;
      }
    });
    console.log('🔧 모든 실제매출 디버깅 활성화');
  },

  // 모든 디버깅 비활성화
  disableAll: () => {
    Object.keys(DEBUG_CONFIG.ACTUAL_SALES).forEach(key => {
      if (key !== 'ENABLED') {
        (DEBUG_CONFIG.ACTUAL_SALES as any)[key] = false;
      }
    });
    console.log('🔧 모든 실제매출 디버깅 비활성화');
  },

  // 성능 관련 디버깅만 활성화
  enablePerformanceOnly: () => {
    ActualSalesDebug.disableAll();
    DEBUG_CONFIG.ACTUAL_SALES.PERFORMANCE = true;
    DEBUG_CONFIG.ACTUAL_SALES.TIMING = true;
    console.log('🔧 성능 관련 디버깅만 활성화');
  },

  // 데이터 플로우 관련 디버깅만 활성화
  enableDataFlowOnly: () => {
    ActualSalesDebug.disableAll();
    DEBUG_CONFIG.ACTUAL_SALES.HOOK = true;
    DEBUG_CONFIG.ACTUAL_SALES.SERVICE = true;
    DEBUG_CONFIG.ACTUAL_SALES.FIREBASE = true;
    DEBUG_CONFIG.ACTUAL_SALES.CALCULATION = true;
    console.log('🔧 데이터 플로우 관련 디버깅만 활성화');
  },

  // 현재 활성화된 타이머 확인
  getActiveTimers: () => {
    const timers = PerformanceTracker.getActiveTimers();
    if (timers.length === 0) {
      console.log('⏱️ 현재 활성화된 타이머가 없습니다');
    } else {
      console.table(timers);
    }
  },

  // 메모리 사용량 확인
  checkMemory: () => {
    ActualSalesDebugLogger.memory('수동 메모리 체크');
  }
};

// 개발 환경에서만 전역 객체로 등록
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).ActualSalesDebug = ActualSalesDebug;
  console.log('🔧 ActualSalesDebug 전역 객체 등록 완료');
  console.log('사용법: ActualSalesDebug.getStatus(), ActualSalesDebug.setModule("FIREBASE", false) 등');
} 